import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { Input } from "@/components/ui/input";
import { Store, MapPin, Search, CheckCircle2, ShoppingBag, Wrench } from "lucide-react";

export default function Directory() {
  const { t, lang } = useLang();
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all"); // all | product | service

  useEffect(() => {
    api.get("/directory/shops").then((r) => {
      setShops(r.data || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const norm = q.trim().toLowerCase();
    return shops.filter((s) => {
      if (filter === "product" && s.products_count === 0) return false;
      if (filter === "service" && s.services_count === 0) return false;
      if (!norm) return true;
      return [s.shop_name, s.owner_name, s.city, s.shop_description]
        .filter(Boolean).some((f) => f.toLowerCase().includes(norm));
    });
  }, [shops, q, filter]);

  const locale = lang === "en" ? "en-GB" : "fr-FR";
  const monthYear = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleDateString(locale, { month: "long", year: "numeric" });
    } catch { return ""; }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-10 sm:py-14" data-testid="directory-page">
      <div className="mb-8 sm:mb-10">
        <span className="label-uppercase text-xs text-[#C85A32]" data-testid="dir-eyebrow">SUKANACOOP</span>
        <h1 className="heading font-extrabold text-3xl sm:text-4xl lg:text-5xl mt-2 text-[#111812]" data-testid="dir-title">{t("dir.title")}</h1>
        <p className="text-[#4B5563] mt-3 max-w-2xl">{t("dir.subtitle")}</p>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between mb-6" data-testid="dir-filters">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4B5563]"/>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("dir.search")} className="pl-9 rounded-full h-11" data-testid="dir-search"/>
        </div>
        <div className="flex gap-2 flex-wrap">
          {[
            { key: "all", label: t("dir.filter_all") },
            { key: "product", label: t("dir.filter_products") },
            { key: "service", label: t("dir.filter_services") },
          ].map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-4 py-2 rounded-full text-sm font-semibold border transition ${
                filter === f.key
                  ? "bg-[#1A3322] text-white border-[#1A3322]"
                  : "bg-white border-[#1A3322]/15 text-[#111812] hover:border-[#1A3322]"
              }`}
              data-testid={`dir-filter-${f.key}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {loading && <p className="text-[#4B5563]">{t("common.loading")}</p>}

      {!loading && filtered.length === 0 && (
        <div className="py-16 text-center bg-white border border-dashed border-[#1A3322]/15 rounded-lg" data-testid="dir-empty">
          <Store className="w-10 h-10 text-[#4B5563] mx-auto mb-3"/>
          <p className="text-[#4B5563]">{t("dir.empty")}</p>
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <>
          <p className="text-sm text-[#4B5563] mb-4" data-testid="dir-count">{filtered.length} {t("dir.count")}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="dir-grid">
            {filtered.map((s) => (
              <ShopCard key={s.id} s={s} t={t} monthYear={monthYear} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function ShopCard({ s, t, monthYear }) {
  const link = s.shop_id
    ? `/boutique/${s.shop_id}`
    : `/recherche?seller_id=${s.id}&kind=${s.services_count > s.products_count ? "service" : "product"}`;

  return (
    <Link
      to={link}
      className="group bg-white border border-[#1A3322]/10 rounded-2xl overflow-hidden hover:border-[#C85A32] hover:shadow-md transition"
      data-testid={`shop-card-${s.id}`}
    >
      <div className="h-28 relative overflow-hidden bg-gradient-to-br from-[#1A3322] via-[#24472F] to-[#C85A32]">
        {s.shop_cover && (
          <img src={s.shop_cover} alt="" className="absolute inset-0 w-full h-full object-cover" data-testid={`shop-cover-${s.id}`}/>
        )}
        {s.shop_official && (
          <span className="absolute top-3 right-3 z-10 inline-flex items-center gap-1 bg-white text-[#1A3322] text-[10px] font-extrabold uppercase tracking-wider px-2 py-1 rounded-full">
            <CheckCircle2 className="w-3 h-3"/>{t("dir.official")}
          </span>
        )}
      </div>
      <div className="p-5 -mt-9 relative">
        <div className="w-14 h-14 rounded-2xl bg-white border-2 border-[#1A3322]/10 flex items-center justify-center overflow-hidden shadow-sm">
          {s.shop_logo
            ? <img src={s.shop_logo} alt={s.shop_name} className="w-full h-full object-contain p-1"/>
            : <span className="heading font-extrabold text-lg text-[#1A3322]">{s.shop_name?.[0] || "?"}</span>
          }
        </div>
        <h3 className="heading font-bold text-lg mt-3 text-[#111812] group-hover:text-[#1A3322] line-clamp-1">{s.shop_name}</h3>
        {s.city && (
          <p className="text-xs text-[#4B5563] flex items-center gap-1 mt-1"><MapPin className="w-3 h-3"/>{s.city}{s.country ? `, ${s.country}` : ""}</p>
        )}
        {s.shop_description && (
          <p className="text-sm text-[#4B5563] mt-2 line-clamp-2">{s.shop_description}</p>
        )}

        <div className="flex flex-wrap gap-2 mt-4">
          {s.products_count > 0 && (
            <span className="inline-flex items-center gap-1 text-xs bg-[#F5F5F0] text-[#111812] px-2.5 py-1 rounded-full font-medium">
              <ShoppingBag className="w-3 h-3"/>{s.products_count} {t("dir.stat_products")}
            </span>
          )}
          {s.services_count > 0 && (
            <span className="inline-flex items-center gap-1 text-xs bg-[#FFF7ED] text-[#C85A32] px-2.5 py-1 rounded-full font-medium">
              <Wrench className="w-3 h-3"/>{s.services_count} {t("dir.stat_services")}
            </span>
          )}
        </div>

        <div className="mt-4 pt-3 border-t border-[#1A3322]/5 flex items-center justify-between">
          {s.joined_at ? (
            <p className="text-[11px] text-[#4B5563]">{t("dir.joined")} {monthYear(s.joined_at)}</p>
          ) : <span/>}
          <span className="text-xs font-bold text-[#C85A32] group-hover:underline">{t("dir.visit")} →</span>
        </div>
      </div>
    </Link>
  );
}
